import SourceDataFormSettingsContext from "@/contexts/form/SourceDataFormSettingsContext";
import useItem from "@/hooks/form/source-data/useItem";
import { Box, alpha, styled } from "@mui/material"
import { FC, PropsWithChildren, ReactNode, useContext } from "react";

const StyledBox = styled(Box)(({theme}) => {
    return {
        display: 'flex',
        whiteSpace: 'pre-wrap',
        minHeight: theme.spacing(5.25),
        borderStyle: 'solid',
        borderWidth: 1,
        borderRadius: theme.shape.borderRadius,
        borderColor: theme.palette.mode === 'dark' ?
            alpha(theme.palette.common.white, 0.12):
            alpha(theme.palette.common.black, 0.12),
        background: theme.palette.mode === 'dark' ?
            theme.palette.grey[800] :
            theme.palette.grey[100]
    }
})

const NoBorderStyles = styled(Box)(({ theme }) => ({
    display: 'flex',
    whiteSpace: 'pre-wrap',
    flex: '1 1 auto',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
}))

const FlexBox = styled(Box)(({ theme }) => ({
    minWidth: theme.spacing(25),
    display: 'flex',
    justifyContent: 'space-between',
    flex: '1 1 auto'
}))

const ContextBox = styled(Box)(({theme}) => {
    return {
        fontSize: `1rem`,
        padding: theme.spacing(1)
    }
})

const EndAdornmentBox = styled(Box)(({theme}) => {
    return {
        padding: theme.spacing(1),
        alignSelf: 'stretch',
        textAlign: 'center',
        minWidth: theme.spacing(5.25),
        borderStyle: 'solid',
        borderWidth: 0,
        borderLeftWidth: 1,
        borderRadius: 0,
        borderColor: theme.palette.mode === 'dark' ?
            alpha(theme.palette.common.white, 0.12):
            alpha(theme.palette.common.black, 0.12),
        background: theme.palette.mode === 'dark' ?
            theme.palette.grey[700] :
            theme.palette.grey[200],
        fontSize: `1.5rem`,
        lineHeight: 1,

        '& svg': {
            fontSize: `1.5rem`
        }
    }
})


const NoBorderStylesStartAdornmentBox = styled(Box)(({ theme }) => ({
    paddingRight: theme.spacing(1),
    marginRight: theme.spacing(2.5),
    minWidth: theme.spacing(3),
    alignSelf: 'stretch',
    textAlign: 'center',
    color: theme.palette.info.main,
    position: 'relative',

    '&:after': {
        content: '"\\2014"',
        display: 'block',
        position: 'absolute',
        top: 0,
        left: '100%',
        bottom: 0,
    }
}))


const NoBorderStylesEndAdornmentBox = styled(Box)(({ theme }) => ({
    alignSelf: 'stretch',
    textAlign: 'center',
    marginLeft: 'auto',
    color: theme.palette.info.main,
    paddingLeft: theme.spacing(1)
}))

const StartAdornmentBox = styled(Box)(({theme}) => {
    return {
        padding: theme.spacing(1),
        alignSelf: 'stretch',
        textAlign: 'center',
        minWidth: theme.spacing(5.25),
        borderStyle: 'solid',
        borderWidth: 0,
        borderRightWidth: 1,
        borderRadius: 0,
        borderColor: theme.palette.mode === 'dark' ?
            alpha(theme.palette.common.white, 0.12):
            alpha(theme.palette.common.black, 0.12),
        background: theme.palette.mode === 'dark' ?
            theme.palette.grey[700] :
            theme.palette.grey[200],
        fontSize: `1.5rem`,
        lineHeight: 1,

        '& svg': {
            fontSize: `1.5rem`
        }
    }
})

export interface ISourceDataItemValueProps {
    itemData?: ReturnType<typeof useItem>;
    hideBorderStyles?: boolean;
    hideStyles?: boolean;
}

interface IProps {
    startAdornment?: ReactNode;
    endAdornment?: ReactNode;
    hideBorderStyles?: boolean;
    hideStyles?: boolean;
}

const SourceDataItemValue: FC<PropsWithChildren<IProps>> = ({
    startAdornment,
    endAdornment,
    hideBorderStyles,
    hideStyles,
    children
}) => {
    if (hideStyles) {
        return (
            <>
                {children}
            </>
        )
    }


    if (hideBorderStyles) {
        return (
            <NoBorderStyles sx={{ minHeight: hideStyles ? 'unset' : undefined }}>
                {
                    startAdornment && (
                        <NoBorderStylesStartAdornmentBox>
                            {startAdornment}
                        </NoBorderStylesStartAdornmentBox>
                    )
                }
                {children}
                {
                    endAdornment && (
                        <NoBorderStylesEndAdornmentBox>
                            {endAdornment}
                        </NoBorderStylesEndAdornmentBox>
                    )
                }
            </NoBorderStyles>
        )
    }

    return (
        <StyledBox>
            {
                startAdornment && (
                    <StartAdornmentBox>
                        {startAdornment}
                    </StartAdornmentBox>
                )
            }
            <FlexBox>
                <ContextBox>
                    {children}
                </ContextBox>
                {
                    endAdornment && (
                        <EndAdornmentBox>
                            {endAdornment}
                        </EndAdornmentBox>
                    )
                }
            </FlexBox>
        </StyledBox>
    );
};

export default SourceDataItemValue;